import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Link as MuiLink,
    Stack,
    Typography,
} from '@mui/material';
import {DeleteOutlined, PlaceOutlined} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {useApplicationContext} from 'ApplicationContext';
import LocationAutocomplete from 'Components/LocationAutocomplete';
import {DeviceQueryModel, GeocodeResultModel} from 'apiClient/Api';

interface DeviceLocationCardProps {
    /** Interne Id der Anlage, deren Standort gepflegt wird. */
    deviceId: string;
    /** Aktueller Stand der Anlage; liefert die angezeigten Standortdaten. */
    device: DeviceQueryModel | null;
    /** Wird nach erfolgreichem Speichern/Entfernen mit dem Server-Stand aufgerufen. */
    onDeviceUpdated: (device: DeviceQueryModel) => void;
    /** Blendet alle Bearbeiten-Aktionen aus und zeigt den Standort nur an. */
    readOnly?: boolean;
}

const formatCoordinates = (latitude: number, longitude: number): string =>
    `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;

/** Link auf die OSM-Karte mit gesetztem Marker. */
const buildMapUrl = (latitude: number, longitude: number): string =>
    `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=17/${latitude}/${longitude}`;

/**
 * Pflegt den Standort einer Anlage: Adresse per Nominatim-Autocomplete suchen,
 * auswählen und speichern. Die Koordinaten des ausgewählten Treffers werden
 * mitgeschickt, freier Text ohne Auswahl wird serverseitig geocodiert.
 */
export const DeviceLocationCard: React.FC<DeviceLocationCardProps> = ({
    deviceId,
    device,
    onDeviceUpdated,
    readOnly,
}) => {
    const {t} = useTranslation();
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();

    const [inputValue, setInputValue] = useState<string>(device?.locationAddress ?? '');
    const [selected, setSelected] = useState<GeocodeResultModel | null>(null);
    const [saving, setSaving] = useState(false);

    const savedAddress = device?.locationAddress ?? '';
    const hasLocation = savedAddress.length > 0;
    const latitude = device?.locationLatitude ?? null;
    const longitude = device?.locationLongitude ?? null;
    const hasCoordinates = latitude !== null && longitude !== null;

    // Übernimmt den Server-Stand, wenn die Anlage (neu) geladen wurde.
    useEffect(() => {
        setInputValue(device?.locationAddress ?? '');
        setSelected(null);
    }, [device?.id, device?.locationAddress]);

    const handleSave = async (): Promise<void> => {
        const address = (selected?.displayName ?? inputValue).trim();
        if (address.length === 0) {
            return;
        }

        setSaving(true);
        try {
            const response = await context.api.device.deviceSetDeviceLocation(deviceId, {
                address,
                latitude: selected?.latitude,
                longitude: selected?.longitude,
                displayName: selected?.displayName,
                postalCode: selected?.postalCode,
                city: selected?.city,
                countryCode: selected?.countryCode,
            });
            onDeviceUpdated(response.data as DeviceQueryModel);
            setSelected(null);
            enqueueSnackbar(t('deviceManagement.licensePage.location.saved'), {variant: 'success'});
        } catch (err: any) {
            if (err?.response?.status === 503) {
                enqueueSnackbar(t('deviceManagement.licensePage.location.errors.geocoderUnavailable'), {variant: 'error'});
            } else if (err?.response?.status === 400) {
                enqueueSnackbar(
                    typeof err?.response?.data === 'string'
                        ? err.response.data
                        : t('deviceManagement.licensePage.location.errors.notResolved'),
                    {variant: 'error'},
                );
            } else {
                enqueueSnackbar(t('deviceManagement.licensePage.location.errors.saveFailed'), {variant: 'error'});
                context.apiError(err);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleClear = async (): Promise<void> => {
        setSaving(true);
        try {
            const response = await context.api.device.deviceClearDeviceLocation(deviceId);
            onDeviceUpdated(response.data as DeviceQueryModel);
            setInputValue('');
            setSelected(null);
            enqueueSnackbar(t('deviceManagement.licensePage.location.cleared'), {variant: 'success'});
        } catch (err: any) {
            enqueueSnackbar(t('deviceManagement.licensePage.location.errors.saveFailed'), {variant: 'error'});
            context.apiError(err);
        } finally {
            setSaving(false);
        }
    };

    const isDirty = useMemo(
        () => selected !== null || inputValue.trim() !== savedAddress,
        [selected, inputValue, savedAddress],
    );

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="h6">
                            {t('deviceManagement.licensePage.location.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                            {t('deviceManagement.licensePage.location.helper')}
                        </Typography>
                    </Box>

                    {!readOnly && (
                        <>
                            <LocationAutocomplete
                                value={inputValue}
                                selected={selected}
                                disabled={saving}
                                helperText={t('deviceManagement.licensePage.location.addressHelper')}
                                onChange={(value, match) => {
                                    setInputValue(value);
                                    setSelected(match);
                                }}
                            />

                            <Stack direction="row" spacing={1} sx={{justifyContent: 'flex-end'}}>
                                {hasLocation && (
                                    <Button
                                        size="small"
                                        color="error"
                                        startIcon={<DeleteOutlined/>}
                                        disabled={saving}
                                        onClick={handleClear}
                                    >
                                        {t('deviceManagement.licensePage.location.clear')}
                                    </Button>
                                )}
                                <Button
                                    size="small"
                                    disabled={saving || !isDirty}
                                    onClick={() => {
                                        setInputValue(savedAddress);
                                        setSelected(null);
                                    }}
                                >
                                    {t('deviceManagement.licensePage.location.cancel')}
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={saving || !isDirty || inputValue.trim().length === 0}
                                    startIcon={saving ? <CircularProgress size={14}/> : <PlaceOutlined/>}
                                    onClick={handleSave}
                                >
                                    {t('deviceManagement.licensePage.location.save')}
                                </Button>
                            </Stack>
                        </>
                    )}

                    {hasLocation ? (
                        <Grid container spacing={2}>
                            <Grid size={{xs: 12, md: 6}}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('deviceManagement.licensePage.location.currentAddress')}
                                </Typography>
                                <Typography variant="body1">
                                    {device?.locationDisplayName ?? savedAddress}
                                </Typography>
                            </Grid>
                            <Grid size={{xs: 12, md: 3}}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('deviceManagement.licensePage.location.coordinates')}
                                </Typography>
                                {hasCoordinates ? (
                                    <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                                        <Typography variant="body1" sx={{fontFamily: 'monospace'}}>
                                            {formatCoordinates(latitude!, longitude!)}
                                        </Typography>
                                        <MuiLink
                                            href={buildMapUrl(latitude!, longitude!)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            variant="body2"
                                        >
                                            {t('deviceManagement.licensePage.location.showOnMap')}
                                        </MuiLink>
                                    </Stack>
                                ) : (
                                    <Chip
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        label={t('deviceManagement.licensePage.location.noCoordinates')}
                                    />
                                )}
                            </Grid>
                            <Grid size={{xs: 12, md: 3}}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('deviceManagement.licensePage.location.place')}
                                </Typography>
                                <Typography variant="body1">
                                    {[device?.locationPostalCode, device?.locationCity]
                                        .filter(Boolean)
                                        .join(' ') || '-'}
                                </Typography>
                            </Grid>
                        </Grid>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {t('deviceManagement.licensePage.location.empty')}
                        </Typography>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default DeviceLocationCard;
